import { api } from "@/services/api"

// ── Types ────────────────────────────────────────────────────────────
// Mirror SlotPlanModels.cs (AB#780) as AmsSlotPlansController serializes them. That controller has
// no string-enum converter, so enums arrive and are sent as numbers; TimeOnly is "HH:mm:ss" and an
// exclusive end of midnight is "00:00:00". The editor model (lib/editorModel.ts) decodes both, and
// also tolerates enum names in case a converter is ever added.

/** System.DayOfWeek: 0 = Sunday … 6 = Saturday. The grid only ever uses 1–5. */
export type DayOfWeekValue = number | string

/** AmsSlotType: 0 public, 1 private, 2 managed. */
export type SlotTypeValue = number | string

/** AmsSlotPlanJobStatus: 0 queued, 1 running, 2 succeeded, 3 failed. */
export type SlotPlanJobStatusValue = number | string

/** AmsSlotPlanKind: 0 site, 1 host. */
export type SlotPlanKindValue = number | string

/** A stretch of one weekday, exclusive end: a host's available cell, or one of a site's breaks. */
export interface SlotPlanWindow {
  weekday: DayOfWeekValue
  /** "HH:mm:ss". */
  start: string
  /** "HH:mm:ss", exclusive; "00:00:00" for midnight. */
  end: string
}

/** One open cell of a site's grid. */
export interface SiteSlotPlanCell extends SlotPlanWindow {
  slotType: SlotTypeValue
}

/** The hours the grid covers (AB#783): "HH:mm", exclusive end ("24:00" for midnight). */
export interface AmsGridWindow {
  start: string
  end: string
}

interface SlotPlanDtoBase {
  teamSlug: string
  /** 30 at a main office, 15 at an outreach site. */
  slotMinutes: number
  window: AmsGridWindow
  lockedBreaks: SlotPlanWindow[]
  activeFrom?: string | null
  activeUntil?: string | null
  maxPerDay?: number | null
  /** Null when nothing is stored yet: the cells are the hours in force. */
  version?: number | null
}

export interface SiteSlotPlanDto extends SlotPlanDtoBase {
  cells: SiteSlotPlanCell[]
}

export interface HostSlotPlanDto extends SlotPlanDtoBase {
  calUserId: number
  cells: SlotPlanWindow[]
}

interface SlotPlanRequestBase {
  /** An instant (ISO 8601); null for now. */
  activeFrom: string | null
  activeUntil: string | null
  maxPerDay: number | null
  /** The version read; a stale one is refused with 409. */
  version: number | null
}

export interface SiteSlotPlanRequest extends SlotPlanRequestBase {
  cells: SiteSlotPlanCell[]
}

export interface HostSlotPlanRequest extends SlotPlanRequestBase {
  cells: SlotPlanWindow[]
}

/** The optional body of restore-default and make-unavailable: a PUT without the grid. */
export type SlotPlanResetRequest = SlotPlanRequestBase

/** 202 from every write. */
export interface SlotPlanJobAccepted {
  jobId: string
}

/** 400 from every write: one sentence per refused cell or setting. */
export interface SlotPlanProblem {
  errors: string[]
}

export interface SlotPlanScheduleResult {
  target: string
  succeeded: boolean
  detail?: string | null
}

export interface SlotPlanJobDto {
  id: string
  kind: SlotPlanKindValue
  teamSlug: string
  calUserId?: number | null
  status: SlotPlanJobStatusValue
  requestedAt: string
  startedAt?: string | null
  finishedAt?: string | null
  results: SlotPlanScheduleResult[]
}

// ── Effective week (AB#802) ──────────────────────────────────────────
// Mirror SlotPlanWeekModels.cs.

/** EffectiveCellState: 0 unavailable, 1 available (a host's cell), 2 public, 3 private, 4 managed (a site's). */
export type EffectiveCellStateValue = number | string

/** AmsScheduleOverrideKind: 0 outreach assignment, 1 out of office, 2 site closure, 3 event-type override. */
export type ScheduleOverrideKindValue = number | string

/** One slot of a dated week. */
export interface SlotPlanWeekCellDto {
  /** "HH:mm:ss", Pacific. */
  start: string
  /** "HH:mm:ss", exclusive; "00:00:00" for midnight. */
  end: string
  /** What the weekly plan alone offers. */
  defaultState: EffectiveCellStateValue
  /** What is offered with CMS's live overrides applied: what CMS publishes to Cal. */
  effectiveState: EffectiveCellStateValue
  /** The overrides (that the caller may see) that changed it; empty when it is the default. */
  overrideIds: string[]
  /** One of the site's locked breaks. */
  isBreak: boolean
}

/** One dated day, Monday to Friday. */
export interface SlotPlanWeekDayDto {
  /** "yyyy-MM-dd", Pacific. */
  date: string
  weekday: DayOfWeekValue
  cells: SlotPlanWeekCellDto[]
}

/** One CMS override in the week. Never a booking. */
export interface SlotPlanWeekOverrideDto {
  id: string
  kind: ScheduleOverrideKindValue
  /** ISO 8601 with the Pacific offset. */
  start: string
  /** ISO 8601 with the Pacific offset; 11:59 PM stands for the end of that day. */
  end: string
  /** The kind as staff read it, e.g. "Out of office". */
  label: string
  /** The site it sends a host to (outreach), otherwise the site it is about; null for every site. */
  targetTeamSlug?: string | null
  targetSiteName?: string | null
  /** The event type forced open, for an event-type override. */
  eventTypeSlug?: string | null
  createdBy?: string | null
  /** Only for a caller with appointments:manage; otherwise null. */
  note?: string | null
}

/** GET …/sites/{team}/week and …/hosts/{team}/{calUserId}/week. */
export interface SlotPlanWeekDto {
  teamSlug: string
  /** The host, for a host's week; null for the site's. */
  calUserId?: number | null
  /** The Monday of the week, "yyyy-MM-dd": whatever date was asked about, normalised. */
  weekStart: string
  slotMinutes: number
  window: AmsGridWindow
  lockedBreaks: SlotPlanWindow[]
  /** The stored plan's version when it is in force that week; informational, saves use the plan's own. */
  planVersion?: number | null
  planActiveFrom?: string | null
  planActiveUntil?: string | null
  days: SlotPlanWeekDayDto[]
  overrides: SlotPlanWeekOverrideDto[]
  /** True without appointments:manage: no notes, and only the overrides the caller may see. */
  restricted: boolean
}

/** The week route of a target; any date in the week ("yyyy-MM-dd"), or omitted for this Pacific week. */
export function slotPlanWeekPath(target: SlotPlanTarget, date?: string): string {
  const query = date ? `?date=${encodeURIComponent(date)}` : ""
  return `${slotPlanPath(target)}/week${query}`
}

/** Which schedule the editor has open: a site's grid, or one host's availability at a site. */
export type SlotPlanTarget =
  | { kind: "site"; teamSlug: string }
  | { kind: "host"; teamSlug: string; calUserId: number }

export type SlotPlanQuickAction = "restore-default" | "make-unavailable"

/** The route of a target under api/ams/slot-plans. */
export function slotPlanPath(target: SlotPlanTarget): string {
  const team = encodeURIComponent(target.teamSlug)
  return target.kind === "site"
    ? `/api/ams/slot-plans/sites/${team}`
    : `/api/ams/slot-plans/hosts/${team}/${target.calUserId}`
}

/** The cache tag a schedule's plan is held under. */
export function planTag(target: SlotPlanTarget) {
  return { type: "AmsSlotPlans" as const, id: slotPlanPath(target) }
}

// ── Endpoints ────────────────────────────────────────────────────────

// A save writes CMS's stored plan at once (the job only carries it to Cal), so every write drops
// the plan it wrote; and once the job lands the calendar's availability changes too, which is what
// "AmsBookings" holds — the page drops that when the job finishes. A dated week also shows CMS's
// overrides (AB#803), so it is held under "AmsScheduleOverrides" too, which creating or withdrawing
// an override drops (scheduleOverridesApi).
const taggedApi = api.enhanceEndpoints({
  addTagTypes: ["AmsSlotPlans", "AmsSlotPlanJobs", "AmsBookings", "AmsScheduleOverrides"],
})

export const availabilityApi = taggedApi.injectEndpoints({
  endpoints: (builder) => ({
    getSiteSlotPlan: builder.query<SiteSlotPlanDto, string>({
      query: (teamSlug) => ({ url: slotPlanPath({ kind: "site", teamSlug }) }),
      providesTags: (_result, _error, teamSlug) => [planTag({ kind: "site", teamSlug })],
    }),
    getHostSlotPlan: builder.query<HostSlotPlanDto, { teamSlug: string; calUserId: number }>({
      query: ({ teamSlug, calUserId }) => ({ url: slotPlanPath({ kind: "host", teamSlug, calUserId }) }),
      providesTags: (_result, _error, { teamSlug, calUserId }) => [planTag({ kind: "host", teamSlug, calUserId })],
    }),
    saveSiteSlotPlan: builder.mutation<SlotPlanJobAccepted, { teamSlug: string; body: SiteSlotPlanRequest }>({
      query: ({ teamSlug, body }) => ({ url: slotPlanPath({ kind: "site", teamSlug }), method: "PUT", body }),
      invalidatesTags: (_result, _error, { teamSlug }) => [planTag({ kind: "site", teamSlug })],
    }),
    saveHostSlotPlan: builder.mutation<
      SlotPlanJobAccepted,
      { teamSlug: string; calUserId: number; body: HostSlotPlanRequest }
    >({
      query: ({ teamSlug, calUserId, body }) => ({
        url: slotPlanPath({ kind: "host", teamSlug, calUserId }),
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { teamSlug, calUserId }) => [planTag({ kind: "host", teamSlug, calUserId })],
    }),
    /** "Restore default" or "Make unavailable": the server builds the grid. */
    resetSlotPlan: builder.mutation<
      SlotPlanJobAccepted,
      { target: SlotPlanTarget; action: SlotPlanQuickAction; body: SlotPlanResetRequest }
    >({
      query: ({ target, action, body }) => ({ url: `${slotPlanPath(target)}/${action}`, method: "POST", body }),
      invalidatesTags: (_result, _error, { target }) => [planTag(target)],
    }),
    /**
     * A site's dated week (AB#802). Dropped with the site's plan, so a save refreshes it, and with the
     * overrides, so creating or withdrawing one does.
     */
    getSiteWeek: builder.query<SlotPlanWeekDto, { teamSlug: string; date?: string }>({
      query: ({ teamSlug, date }) => ({ url: slotPlanWeekPath({ kind: "site", teamSlug }, date) }),
      providesTags: (_result, _error, { teamSlug }) => [planTag({ kind: "site", teamSlug }), "AmsScheduleOverrides"],
    }),
    /** One host's dated week at a site (AB#802), dropped as a site's is. */
    getHostWeek: builder.query<SlotPlanWeekDto, { teamSlug: string; calUserId: number; date?: string }>({
      query: ({ teamSlug, calUserId, date }) => ({ url: slotPlanWeekPath({ kind: "host", teamSlug, calUserId }, date) }),
      providesTags: (_result, _error, { teamSlug, calUserId }) => [
        planTag({ kind: "host", teamSlug, calUserId }),
        "AmsScheduleOverrides",
      ],
    }),
    getSlotPlanJob: builder.query<SlotPlanJobDto, string>({
      query: (id) => ({ url: `/api/ams/slot-plans/jobs/${encodeURIComponent(id)}` }),
      providesTags: (_result, _error, id) => [{ type: "AmsSlotPlanJobs" as const, id }],
    }),
  }),
})

export const {
  useGetSiteSlotPlanQuery,
  useGetHostSlotPlanQuery,
  useSaveSiteSlotPlanMutation,
  useSaveHostSlotPlanMutation,
  useResetSlotPlanMutation,
  useGetSlotPlanJobQuery,
  useGetSiteWeekQuery,
  useGetHostWeekQuery,
} = availabilityApi
